import AddIcon from '@mui/icons-material/Add'
import ConstructionIcon from '@mui/icons-material/Construction'
import EventIcon from '@mui/icons-material/Event'
import PersonIcon from '@mui/icons-material/Person'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  Stack
} from '@mui/material'
import { useCallback, useContext, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { FeatureFlags } from '../../models/FeatureFlags'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import {
  useGetCustomerVehiclesQuery,
  useGetRepairOrdersQuery
} from '../../redux/api'
import { CustomerEntry, VehicleEntry } from '../../sdk'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import { OutlinedBox } from '../SharedUI'
import { NewVehicleTabs } from '../Vehicles/NewVehicleTabs'
import { AppointmentByCustomer } from './AppointmentByCustomer'
import { CustomerDeleteMenu } from './CustomerDeleteMenu'
import { CustomerDetailForm } from './CustomerDetailForm'
import { CustomerNotesForm } from './CustomerNotesForm'
import { RepairOrderByCustomer } from './RepairOrderByCustomer'
import { VehiclesByCustomer } from './VehiclesByCustomer'

export const CustomerDetailsContainer = ({
  customer
}: {
  customer: CustomerEntry
}) => {
  const { featureFlags } = useContext(AppContext)
  const isReadOnly = featureFlags && featureFlags[FeatureFlags.ReadOnly]

  const navigate = useNavigate()
  const [showNewVehicleDialog, setShowNewVehicleDialog] = useState(false)
  const customerId = customer.id!
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const defaultNumResults = 10
  const [numResults, setNumResults] = useState(defaultNumResults)
  const { data: vehicles } = useGetCustomerVehiclesQuery({
    authorization: accessToken,
    shop: shopId,
    customer: customerId!,
    numResults
  })
  const { data: repairOrders } = useGetRepairOrdersQuery({
    authorization: accessToken,
    shop: shopId,
    customer: [customerId!]
  })

  const submitNewVehicleCallback = useCallback(
    async (vehicle: VehicleEntry) => {
      navigate(AppRoutes.VEHICLES + '/' + vehicle.id)
    },
    [navigate]
  )

  return (
    <>
      <Dialog
        maxWidth="md"
        fullWidth
        open={showNewVehicleDialog}
        onClose={() => {
          setShowNewVehicleDialog(false)
        }}
        aria-label="createNewVehicleDialog">
        {isReadOnly && (
          <Alert severity="warning" variant="filled">
            READ ONLY
          </Alert>
        )}

        <DialogTitle>Add a Vehicle</DialogTitle>
        <NewVehicleTabs
          customerId={customerId!}
          submitCallback={submitNewVehicleCallback}
          setShowForm={setShowNewVehicleDialog}
        />
      </Dialog>
      <Grid container spacing={1} paddingBottom="50px">
        <Grid item xs={12} lg={8}>
          <OutlinedBox
            title={
              formatName(customer.information) +
              (customer.information!.archived ? ' (Deleted)' : '')
            }
            actionsRight={
              <Stack direction="row" spacing={1}>
                <ButtonGroup
                  size="small"
                  variant="outlined"
                  aria-label="outlined button group">
                  <Button
                    aria-label="createAppointmentButton"
                    startIcon={<EventIcon />}
                    onClick={() =>
                      navigate(
                        generatePath(AppRoutes.MANAGE_APPOINTMENTS, {
                          id: 'new'
                        }) +
                          '?customerId=' +
                          customerId
                      )
                    }>
                    Create Appointment
                  </Button>
                  <Button
                    startIcon={<ReceiptLongIcon />}
                    aria-label="createEstimateButton"
                    onClick={() =>
                      navigate(
                        AppRoutes.ESTIMATES + '/new?customerId=' + customerId
                      )
                    }>
                    Create Estimate
                  </Button>
                  <Button
                    startIcon={<ConstructionIcon />}
                    onClick={() =>
                      navigate(
                        AppRoutes.ESTIMATES +
                          '/new?isRepairOrder=yes&&customerId=' +
                          customerId
                      )
                    }>
                    Create Repair Order
                  </Button>
                </ButtonGroup>
                <CustomerDeleteMenu customer={customer} />
              </Stack>
            }
            logo={
              <Icon sx={{ color: 'grey.500' }}>
                <PersonIcon />
              </Icon>
            }>
            <CustomerDetailForm customer={customer} />
            <Divider />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}>
              {vehicles && <VehiclesByCustomer vehicles={vehicles} />}
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="outlined"
                sx={{ width: '100%', maxWidth: '12rem' }}
                onClick={() => setShowNewVehicleDialog(true)}>
                Add New Vehicle
              </Button>
            </Stack>
            <Button
              onClick={() =>
                numResults === defaultNumResults
                  ? setNumResults(9999)
                  : setNumResults(defaultNumResults)
              }>
              {numResults === defaultNumResults
                ? 'See more vehicles'
                : 'See fewer vehicles'}
            </Button>
            <Divider />
            <CustomerNotesForm customer={customer} />
            <RepairOrderByCustomer
              vehicles={vehicles}
              repairOrders={repairOrders}
            />
          </OutlinedBox>
        </Grid>
        <Grid item xs={12} lg={4}>
          <AppointmentByCustomer customer={customer} />
        </Grid>
      </Grid>
    </>
  )
}
